import React, { useEffect, useState } from "react";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import { ColumnConfig } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { getMostRecentWednesday } from "@app/shared/utility/getMostRecentDay";
import OperationsService from "@app/operations/operationsService";
import { WeeklyJobStatisticsResult } from "@app/operations/jobStatisticsResult";

interface WeeklyComparisonProps {
  clients: { clientId: number; name: string }[];
  clientId: number | null;
  setClientId: React.Dispatch<React.SetStateAction<number | null>>;
}

const operationsService = new OperationsService();
const CLIENT_SELECT_ERROR_TEXT = "Please select a Client";
const ALL_LINES_OF_BUSINESS = "all";
const OTHER_LINES_OF_BUSINESS = "other";
const LOB_SELECT_ERROR_TEXT = "Please select a Line of Business";

const uhcColumns: ColumnConfig = {
  avgSeconds: {
    displayText: "Average Seconds",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__average-seconds-output-uhc",
  },
  jobCount: {
    displayText: "Job Count",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__job-count-output-uhc",
  },
  lineOfBusiness: {
    displayText: "Line Of Business",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__lob-output-uhc",
  },
  yrWkNum: {
    displayText: "Year Week Number",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__yrWkNum-output-uhc",
  },
};

const WeeklyComparison: React.FC<WeeklyComparisonProps> = ({ clients, clientId, setClientId }) => {
  const [jobStatistics, setJobStatistics] = useState<WeeklyJobStatisticsResult[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [lineOfBusiness, setLineOfBusiness] = useState<string | null>(ALL_LINES_OF_BUSINESS);
  const [lineOfBusinessOptions, setLineOfBusinessOptions] = useState<string[]>([]);
  const [runDate, setRunDate] = useState<string>(getMostRecentWednesday());


  useEffect(() => {
    calculateJobStatistics();
  }, []);

  useEffect(() => {
    resetFormValues();
    if (clientId == null) {
      return;
    }

    operationsService
      .getLinesOfBusiness(clientId, true)
      .then(
        linesOfBusiness => {
          setLineOfBusinessOptions(linesOfBusiness);
          if (linesOfBusiness.length === 0) setError("No Line of Business options available for this client.");
        },
        () => setError("Error fetching Line of Business options")
      );
  }, [clientId]);

  const resetFormValues = () => {
    setError(null);
    setLineOfBusinessOptions([]);
    setLineOfBusiness(ALL_LINES_OF_BUSINESS);
    setJobStatistics(null);
  };

  const calculateJobStatistics = async () => {
    setJobStatistics(null);
    setLoading(true);
    setError(null);
    const adjustedLineOfBusiness = lineOfBusiness === ALL_LINES_OF_BUSINESS ? "" : lineOfBusiness;
    try {
      const response = await operationsService.getWeeklyJobStatistics(
        clientId,
        adjustedLineOfBusiness,
        runDate
      );
      setJobStatistics(response);
    } catch (error) {
      setError("Error calculating job statistics");
    } finally {
      setLoading(false);
    }
  };

  const isClientSelectionInvalid = clientId === null;
  const isLOBSelectionInvalid = lineOfBusiness === null;

  let calculateDisabledTooltipText = "";
  if (isClientSelectionInvalid)
    calculateDisabledTooltipText = CLIENT_SELECT_ERROR_TEXT;
  else if (isLOBSelectionInvalid)
    calculateDisabledTooltipText = LOB_SELECT_ERROR_TEXT;

  return (
    <div>
      <div className="form-row mb-3">
        <div className="col pe-1">
          <QESSelect
            label="Client"
            id="clientSelect"
            value={clientId ?? ""}
            onChange={(event) => setClientId(Number(event.target.value))}
            placeholder={clients.length === 0 ? "Loading Clients..." : "Select Client"}
            testId="weekend-run-monitoring__client-select-uhc"
            required
            disabled={clients.length === 0}
            isValid={!isClientSelectionInvalid}
            errorText={CLIENT_SELECT_ERROR_TEXT}
            positionDescriptionTextAbsolutely={true}
          >
            {clients && clients.map((client) => (
              <option key={client.clientId} value={client.clientId.toString()}>
                {client.name}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col px-1">
          <QESSelect
            id="lineOfBusinessSelect"
            value={lineOfBusiness ?? ""}
            onChange={(event) => setLineOfBusiness(event.target.value)}
            placeholder="Select a Line Of Business"
            testId="weekend-run-monitoring__line-of-business-select-uhc"
            label="Line of Business"
            disabled={lineOfBusinessOptions.length === 0}
            isValid={lineOfBusinessOptions.length === 0 || !isLOBSelectionInvalid}
            errorText={LOB_SELECT_ERROR_TEXT}
            required
            positionDescriptionTextAbsolutely={true}
          >
            {lineOfBusiness.length > 0 && (<option value={ALL_LINES_OF_BUSINESS}>All</option>)}
            {lineOfBusinessOptions.map((lob) => (
              <option key={lob.toLowerCase()} value={lob.toLowerCase()}>
                {lob.toUpperCase()}
              </option>
            ))}
            {lineOfBusiness.length > 0 && (<option value={OTHER_LINES_OF_BUSINESS}>Other</option>)}
          </QESSelect>
        </div>
        <div className="col px-1">
          <QESDateInput
            label="Run Date"
            value={runDate}
            onChange={(event) => setRunDate(event.target.value)}
            testId="weekend-run-monitoring__run-date-input-uhc"
          />
        </div>
        <div className="col d-flex align-items-end ps-1">
          <TooltipWrapper position="top" text={calculateDisabledTooltipText}>
            <QESButton
              onClick={calculateJobStatistics}
              qesType="primary"
              qesTestId="weekly-run-monitoring__calculate-weekly-job-statistics-uhc"
              disabled={!!calculateDisabledTooltipText}
            >
              Search
            </QESButton>
          </TooltipWrapper>
        </div>
      </div>
      <div className="mt-4">
        {loading && <LoadingSpinner />}
        {error && <div>{error}</div>}
        {jobStatistics && (
          <Waffles
            columns={uhcColumns}
            data={jobStatistics.map((stat, index) => ({
              /* oxlint-disable sort-keys */
              key: index.toString(),
              yrWkNum: stat.yrWkNum,
              lineOfBusiness: stat.lineOfBusiness,
              jobCount: stat.jobCount,
              avgSeconds: stat.avgSeconds,
              /* oxlint-enable sort-keys */
            }))}
            enablePagination={false}
            passThroughSorting={true}
            testId="weekend-run-monitoring__job-statistics-table-uhc"
          />
        )}
      </div>
    </div>
  );
};

export default WeeklyComparison;